import api from '../lib/api';

class AuthService {
  async login(email: string, password: string, tenantCode?: string) {
    try {
      // Prepare login request with tenant validation
      const loginRequest: any = { email, password };

      // Include Institution Code (tenant code) for validation if provided
      if (tenantCode) {
        loginRequest.tenantCode = tenantCode.trim().toUpperCase();
      }

      const response = await api.post('/auth/login', loginRequest);
      const data = response.data;

      // Store auth data
      localStorage.setItem('token', data.token);
      localStorage.setItem('userRole', data.roles?.includes('ROLE_ADMIN') ? 'ADMIN' : 'USER');
      localStorage.setItem('userName', data.name || data.username);
      localStorage.setItem('userEmail', data.email);
      localStorage.setItem('isFirstTimeLogin', data.isFirstTimeLogin ? 'true' : 'false');

      return data;
    } catch (error: any) {
      console.error('AuthService login error:', error);
      throw new Error('Invalid credentials or tenant context');
    }
  }

  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('isFirstTimeLogin');
  }

  isAuthenticated() {
    return !!localStorage.getItem('token');
  }

  getUser() {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('userRole');
    const name = localStorage.getItem('userName');

    if (!token) return null;

    return {
      token,
      role,
      name
    };
  }

  async changePasswordFirstTime(currentPassword: string, newPassword: string, userId: string) {
    try {
      const response = await api.post('/auth/change-password-first-time', {
        currentPassword,
        newPassword,
        userId
      });

      // Clear first-time login flag on successful password change
      localStorage.setItem('isFirstTimeLogin', 'false');

      return response.data;
    } catch (error: any) {
      console.error('AuthService changePasswordFirstTime error:', error);
      throw error;
    }
  }

  async validateResetToken(token: string) {
    try {
      const response = await api.get(`/auth/validate-reset-token?token=${encodeURIComponent(token)}`);
      return response.data;
    } catch (error: any) {
      console.error('AuthService validateResetToken error:', error);
      throw error;
    }
  }

  async resetPasswordWithToken(token: string, newPassword: string) {
    try {
      const response = await api.post('/auth/reset-password-with-token', {
        token,
        newPassword
      });

      // Clear first-time login flag on successful password change
      localStorage.setItem('isFirstTimeLogin', 'false');

      return response.data;
    } catch (error: any) {
      console.error('AuthService resetPasswordWithToken error:', error);
      throw error;
    }
  }

  async forgotPasswordAdmin(email: string, tenantCode: string) {
    try {
      const response = await api.post('/auth/forgot-password/admin', {
        email,
        tenantCode
      });
      return response.data;
    } catch (error: any) {
      console.error('AuthService forgotPasswordAdmin error:', error);
      throw error;
    }
  }

  // Alias for compatibility with ResetPasswordPage
  async resetPassword(token: string, newPassword: string) {
    return this.resetPasswordWithToken(token, newPassword);
  }

  async changePassword(currentPassword: string, newPassword: string) {
    try {
      const response = await api.post('/auth/change-password', {
        currentPassword,
        newPassword
      });
      return response.data;
    } catch (error: any) {
      console.error('AuthService changePassword error:', error);
      throw error;
    }
  }

  async authenticatedFetch(url: string, options: any = {}) {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('No authentication token found');
    }

    // Use the api instance which includes tenant headers
    return api.request({
      url,
      method: options.method || 'GET',
      data: options.body ? JSON.parse(options.body) : options.data,
      headers: {
        'Authorization': `Bearer ${token}`,
        ...options.headers
      },
      ...options
    });
  }
}

export default new AuthService();
